import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import {
  Edit2,
  Loader2,
  Plus,
  Search,
  ToggleLeft,
  ToggleRight,
  UserPlus,
} from "lucide-react"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Checkbox } from "shared/ui/checkbox"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "shared/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Switch } from "shared/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { searchCompanies, type CompanyDto } from "modules/admin/api/companyService"
import { getTenants, type Tenant } from "shared/api/tenantService"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"
import {
  searchWorkflowEmployees,
  type WorkflowEmployeeLookupDto,
} from "modules/admin/api/workflowConfigurationService"
import {
  createUser,
  searchUsers,
  updateUser,
  type UserAdminDto,
} from "modules/admin/api/userService"

interface CreateUserAccountProps {
  currentUser?: { name?: string }
}

type UserFormState = {
  username: string
  fullName: string
  email: string
  phone: string
  userType: string
  authProvider: string
  tenantIds: string[]
  companyId: string
  profileMapping: string
  password: string
  confirmPassword: string
  isActive: boolean
}

const TENANT_ID = DEFAULT_TENANT_ID

const USER_TYPES = [
  { value: "EMPLOYEE", label: "Employee" },
  { value: "CONTRACTOR", label: "Contractor" },
]

const AUTH_PROVIDERS = [
  { value: "LOCAL", label: "LOCAL" },
  { value: "AD", label: "AD" },
  { value: "AZURE", label: "AZURE" },
]

const NO_COMPANY = "__none__"
const NO_EMPLOYEE = "__none__"

const initialForm: UserFormState = {
  username: "",
  fullName: "",
  email: "",
  phone: "",
  userType: "EMPLOYEE",
  authProvider: "LOCAL",
  tenantIds: [TENANT_ID],
  companyId: NO_COMPANY,
  profileMapping: NO_EMPLOYEE,
  password: "",
  confirmPassword: "",
  isActive: true,
}

const formatDate = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toISOString().slice(0, 10)
}

export function CreateUserAccount({ currentUser }: CreateUserAccountProps) {
  const [items, setItems] = useState<UserAdminDto[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState("")
  const [userTypeFilter, setUserTypeFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")
  const [isLoading, setIsLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<UserAdminDto | null>(null)
  const [form, setForm] = useState<UserFormState>(initialForm)

  const [companyOptions, setCompanyOptions] = useState<CompanyDto[]>([])
  const [companySearch, setCompanySearch] = useState("")
  const [isCompanyLoading, setIsCompanyLoading] = useState(false)
  const [employeeOptions, setEmployeeOptions] = useState<WorkflowEmployeeLookupDto[]>([])
  const [employeeSearch, setEmployeeSearch] = useState("")
  const [isEmployeeLoading, setIsEmployeeLoading] = useState(false)
  const [tenantOptions, setTenantOptions] = useState<Tenant[]>([])
  const [tenantSearch, setTenantSearch] = useState("")
  const [isTenantLoading, setIsTenantLoading] = useState(false)

  const isEditing = Boolean(editing)
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])

  const loadUsers = useCallback(
    async (currentPage = 1) => {
      setIsLoading(true)
      try {
        const result = await searchUsers({
          tenantId: TENANT_ID,
          search: searchTerm || undefined,
          userType: userTypeFilter === "all" ? undefined : userTypeFilter,
          isActive: statusFilter === "all" ? undefined : statusFilter === "active",
          page: currentPage,
          pageSize,
        })
        setItems(result.items ?? [])
        setTotalItems(result.totalItems ?? result.items.length)
        setPage(result.pageNumber ?? currentPage)
      } catch (error) {
        console.error(error)
        toast.error("โหลดรายการผู้ใช้ไม่สำเร็จ")
      } finally {
        setIsLoading(false)
      }
    },
    [searchTerm, userTypeFilter, statusFilter, pageSize],
  )

  const handleClearFilters = () => {
    setSearchTerm("")
    setUserTypeFilter("all")
    setStatusFilter("all")
    loadUsers(1)
  }

  const handlePageSizeChange = (value: string) => {
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed)) return
    setPageSize(parsed)
    loadUsers(1)
  }

  const loadCompanies = useCallback(async () => {
    try {
      const result = await searchCompanies({
        tenantId: TENANT_ID,
        status: "active",
        page: 1,
        pageSize: 200,
      })
      setCompanyOptions(result.items ?? [])
    } catch (error) {
      console.error(error)
      toast.error("โหลดรายชื่อบริษัทไม่สำเร็จ")
    }
  }, [])

  useEffect(() => {
    loadUsers(1)
  }, [loadUsers])

  useEffect(() => {
    if (!dialogOpen) return
    const handler = window.setTimeout(() => {
      const term = companySearch.trim()
      setIsCompanyLoading(true)
      searchCompanies({
        tenantId: TENANT_ID,
        status: "active",
        search: term || undefined,
        page: 1,
        pageSize: 50,
      })
        .then((result) => {
          setCompanyOptions(result.items ?? [])
        })
        .catch((error) => {
          console.error(error)
          setCompanyOptions([])
          toast.error("โหลดรายชื่อบริษัทไม่สำเร็จ")
        })
        .finally(() => {
          setIsCompanyLoading(false)
        })
    }, 400)

    return () => {
      window.clearTimeout(handler)
    }
  }, [dialogOpen, companySearch])

  const selectedEmployee = useMemo(
    () => employeeOptions.find((item) => item.employeeId === form.profileMapping) ?? null,
    [employeeOptions, form.profileMapping],
  )

  const selectedTenantsLabel = useMemo(() => {
    const selectedTenantIds = form.tenantIds ?? []
    if (selectedTenantIds.length === 0) return "Select tenant"

    const selectedNames = selectedTenantIds
      .map((tenantId) => tenantOptions.find((item) => item.tenantId === tenantId)?.name ?? tenantId)
      .filter((name, index, self) => name && self.indexOf(name) === index)

    if (selectedNames.length === 0) return `${selectedTenantIds.length} tenant(s)`
    if (selectedNames.length <= 2) return selectedNames.join(", ")
    return `${selectedNames[0]}, ${selectedNames[1]} +${selectedNames.length - 2}`
  }, [form.tenantIds, tenantOptions])

  const filteredTenantOptions = useMemo(() => {
    const term = tenantSearch.trim().toLowerCase()
    if (!term) return tenantOptions
    return tenantOptions.filter((item) =>
      `${item.name} ${item.tenantId}`.toLowerCase().includes(term),
    )
  }, [tenantOptions, tenantSearch])

  const selectedCompanyLabel = useMemo(() => {
    if (!form.companyId || form.companyId === NO_COMPANY) return null
    const match = companyOptions.find((company) => company.companyId === form.companyId)
    if (match) return `${match.name} (${match.code})`
    if (editing?.companyId === form.companyId && editing.companyName) return editing.companyName
    if (selectedEmployee?.companyId === form.companyId && selectedEmployee.companyName) return selectedEmployee.companyName
    return form.companyId
  }, [companyOptions, editing, form.companyId, selectedEmployee])

  useEffect(() => {
    if (!dialogOpen || form.userType !== "EMPLOYEE") {
      return
    }

    const handler = window.setTimeout(() => {
      const term = employeeSearch.trim()
      setIsEmployeeLoading(true)
      searchWorkflowEmployees(TENANT_ID, term || undefined, 1, 20)
        .then((result) => {
          setEmployeeOptions(result.items ?? [])
        })
        .catch((error) => {
          console.error(error)
          setEmployeeOptions([])
          toast.error("โหลดรายชื่อพนักงานไม่สำเร็จ")
        })
        .finally(() => {
          setIsEmployeeLoading(false)
        })
    }, 400)

    return () => {
      window.clearTimeout(handler)
    }
  }, [dialogOpen, employeeSearch, form.userType])

  useEffect(() => {
    if (!dialogOpen || form.userType !== "EMPLOYEE") {
      return
    }

    if (!form.profileMapping || form.profileMapping === NO_EMPLOYEE) {
      return
    }

    if (employeeOptions.some((item) => item.employeeId === form.profileMapping)) {
      return
    }

    let cancelled = false
    setIsEmployeeLoading(true)
    searchWorkflowEmployees(TENANT_ID, form.profileMapping, 1, 5)
      .then((result) => {
        if (cancelled) return
        const matched = (result.items ?? []).find((item) => item.employeeId === form.profileMapping)
        if (!matched) return
        setEmployeeOptions((prev) =>
          prev.some((item) => item.employeeId === matched.employeeId) ? prev : [matched, ...prev],
        )
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        if (!cancelled) setIsEmployeeLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [dialogOpen, form.profileMapping, form.userType, employeeOptions])

  useEffect(() => {
    if (form.userType !== "EMPLOYEE" && form.profileMapping !== NO_EMPLOYEE) {
      setForm((prev) => ({ ...prev, profileMapping: NO_EMPLOYEE }))
      setEmployeeSearch("")
      setEmployeeOptions([])
    }
  }, [form.userType, form.profileMapping])

  useEffect(() => {
    if (!dialogOpen) return
    let cancelled = false
    setIsTenantLoading(true)
    getTenants()
      .then((result) => {
        if (cancelled) return
        setTenantOptions(result ?? [])
      })
      .catch((error) => {
        console.error(error)
        if (cancelled) return
        setTenantOptions([])
        toast.error("โหลดรายการ tenant ไม่สำเร็จ")
      })
      .finally(() => {
        if (!cancelled) {
          setIsTenantLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [dialogOpen])

  const toggleTenantSelection = useCallback((tenantId: string, checked: boolean) => {
    setForm((prev) => {
      const current = prev.tenantIds ?? []
      if (checked) {
        if (current.includes(tenantId)) return prev
        return { ...prev, tenantIds: [...current, tenantId] }
      }

      const next = current.filter((item) => item !== tenantId)
      return { ...prev, tenantIds: next }
    })
  }, [])

  const openCreate = () => {
    setEditing(null)
    setForm(initialForm)
    setTenantSearch("")
    setCompanySearch("")
    setCompanyOptions([])
    setEmployeeSearch("")
    setEmployeeOptions([])
    setDialogOpen(true)
  }

  const openEdit = (item: UserAdminDto) => {
    const selectedTenantIds =
      item.tenantIds && item.tenantIds.length > 0
        ? item.tenantIds
        : [item.tenantId ?? TENANT_ID]

    setEditing(item)
    setForm({
      username: item.username ?? "",
      fullName: item.fullName ?? "",
      email: item.email ?? "",
      phone: item.phone ?? "",
      userType: item.userType ?? "EMPLOYEE",
      authProvider: item.authProvider ?? "AD",
      tenantIds: selectedTenantIds,
      companyId: item.companyId ?? NO_COMPANY,
      profileMapping: item.profileMapping ?? NO_EMPLOYEE,
      password: "",
      confirmPassword: "",
      isActive: item.isActive,
    })
    setTenantSearch("")
    setCompanySearch("")
    setCompanyOptions([])
    setEmployeeSearch("")
    setEmployeeOptions([])
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!form.username.trim() || !form.fullName.trim()) {
      toast.error("กรุณากรอก Username และชื่อผู้ใช้")
      return
    }

    if (form.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(form.email.trim())) {
        toast.error("รูปแบบอีเมลไม่ถูกต้อง")
        return
      }
    }

    if (form.authProvider === "LOCAL") {
      if (!isEditing && !form.password.trim()) {
        toast.error("กรุณากรอกรหัสผ่าน")
        return
      }
      if (form.password !== form.confirmPassword) {
        toast.error("รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน")
        return
      }
    }

    const tenantIds = Array.from(
      new Set(
        (form.tenantIds ?? [])
          .map((item) => item.trim())
          .filter((item) => item.length > 0),
      ),
    )

    if (tenantIds.length === 0) {
      toast.error("กรุณาเลือก tenant อย่างน้อย 1 รายการ")
      return
    }

    const payload = {
      tenantId: TENANT_ID,
      tenantIds,
      username: form.username.trim(),
      fullName: form.fullName.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      userType: form.userType,
      authProvider: form.authProvider,
      companyId: form.companyId && form.companyId !== NO_COMPANY ? form.companyId : null,
      profileMapping: form.profileMapping && form.profileMapping !== NO_EMPLOYEE ? form.profileMapping : null,
      password: form.authProvider === "LOCAL" && form.password.trim() ? form.password.trim() : null,
      isActive: form.isActive,
    }

    try {
      if (isEditing && editing) {
        await updateUser(editing.userId, payload)
        toast.success("อัปเดตข้อมูลผู้ใช้สำเร็จ")
      } else {
        await createUser(payload)
        toast.success("สร้างผู้ใช้สำเร็จ")
      }
      setDialogOpen(false)
      setForm(initialForm)
      loadUsers(page)
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data ?? "บันทึกข้อมูลผู้ใช้ไม่สำเร็จ"
      toast.error(typeof message === "string" ? message : "บันทึกข้อมูลผู้ใช้ไม่สำเร็จ")
    }
  }

  const handleToggleActive = async (item: UserAdminDto) => {
    try {
      await updateUser(item.userId, {
        tenantId: TENANT_ID,
        tenantIds:
          item.tenantIds && item.tenantIds.length > 0
            ? item.tenantIds
            : [item.tenantId ?? TENANT_ID],
        username: item.username ?? "",
        fullName: item.fullName ?? "",
        email: item.email ?? null,
        phone: item.phone ?? null,
        userType: item.userType ?? "EMPLOYEE",
        authProvider: item.authProvider ?? "AD",
        companyId: item.companyId ?? null,
        profileMapping: item.profileMapping ?? null,
        password: null,
        isActive: !item.isActive,
      })
      toast.success(`เปลี่ยนสถานะเป็น ${!item.isActive ? "Active" : "Inactive"}`)
      loadUsers(page)
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data ?? "อัปเดตสถานะไม่สำเร็จ"
      toast.error(typeof message === "string" ? message : "อัปเดตสถานะไม่สำเร็จ")
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <UserPlus className="h-5 w-5 text-blue-600" />
              Create User Account
            </CardTitle>
            <p className="text-sm text-gray-500">
              จัดการบัญชีผู้ใช้งาน (admin: {currentUser?.name ?? "-"})
            </p>
          </div>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> เพิ่มผู้ใช้
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full md:w-80">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
              <Input
                placeholder="ค้นหา username / ชื่อ / อีเมล"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && loadUsers(1)}
                className="pl-9"
              />
            </div>
            <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="User Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกประเภท</SelectItem>
                {USER_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => loadUsers(1)} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "ค้นหา"}
            </Button>
            <Button variant="ghost" onClick={handleClearFilters} disabled={isLoading}>
              Clear filters
            </Button>
            <div className="text-sm text-gray-500">
              ทั้งหมด {totalItems.toLocaleString("th-TH")} รายการ
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ผู้ใช้</TableHead>
                  <TableHead>User Type</TableHead>
                  <TableHead>Auth</TableHead>
                  <TableHead>บริษัท</TableHead>
                  <TableHead>สถานะ</TableHead>
                  <TableHead>สร้างเมื่อ</TableHead>
                  <TableHead className="text-right">จัดการ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                      กำลังโหลดข้อมูล...
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                      ไม่พบข้อมูล
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.userId}>
                      <TableCell>
                        <div className="font-medium">{item.fullName ?? "-"}</div>
                        <div className="text-xs text-gray-500">{item.username ?? item.email ?? "-"}</div>
                      </TableCell>
                      <TableCell>{item.userType ?? "-"}</TableCell>
                      <TableCell>{item.authProvider ?? "-"}</TableCell>
                      <TableCell>{item.companyName ?? "-"}</TableCell>
                      <TableCell>
                        <Badge variant={item.isActive ? "default" : "secondary"}>
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(item.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="gap-1">
                            <Edit2 className="h-4 w-4" /> แก้ไข
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(item)}
                            className="gap-1"
                          >
                            {item.isActive ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-gray-500" />
                            )}
                            {item.isActive ? "ปิด" : "เปิด"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>หน้า {page} / {totalPages}</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => loadUsers(page - 1)}
              >
                ก่อนหน้า
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => loadUsers(page + 1)}
              >
                ถัดไป
              </Button>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Per page</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[90px]">
                    <SelectValue placeholder="20" />
                  </SelectTrigger>
                  <SelectContent>
                    {['10', '20', '50', '100'].map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? "แก้ไขผู้ใช้" : "เพิ่มผู้ใช้"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input
                  value={form.username}
                  onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
                  placeholder="username"
                />
              </div>
              <div className="space-y-2">
                <Label>ชื่อ-นามสกุล *</Label>
                <Input
                  value={form.fullName}
                  onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
                  placeholder="ชื่อ นามสกุล"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  placeholder="user@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                  placeholder="0xx-xxx-xxxx"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>User Type</Label>
                <Select value={form.userType} onValueChange={(value) => setForm((prev) => ({ ...prev, userType: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="User Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Auth Provider</Label>
                <Select value={form.authProvider} onValueChange={(value) => setForm((prev) => ({ ...prev, authProvider: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Auth Provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {AUTH_PROVIDERS.map((provider) => (
                      <SelectItem key={provider.value} value={provider.value}>
                        {provider.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tenant Mapping *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between text-left font-normal">
                    <span className="line-clamp-1">{selectedTenantsLabel}</span>
                    <span className="text-xs text-gray-500">{form.tenantIds.length} selected</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[420px] p-3" align="start">
                  <div className="space-y-2">
                    <Input
                      placeholder="Search tenant"
                      value={tenantSearch}
                      onChange={(event) => setTenantSearch(event.target.value)}
                    />
                    <div className="max-h-56 space-y-2 overflow-y-auto rounded border p-2">
                      {isTenantLoading ? (
                        <div className="text-sm text-gray-500">Loading...</div>
                      ) : filteredTenantOptions.length === 0 ? (
                        <div className="text-sm text-gray-500">No tenant found</div>
                      ) : (
                        filteredTenantOptions.map((tenant) => {
                          const checked = form.tenantIds.includes(tenant.tenantId)
                          return (
                            <label
                              key={tenant.tenantId}
                              className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-gray-50"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(value) => toggleTenantSelection(tenant.tenantId, value === true)}
                              />
                              <span className="text-sm">
                                <span className="font-medium">{tenant.name}</span>
                                <span className="block text-xs text-gray-500">{tenant.tenantId}</span>
                              </span>
                            </label>
                          )
                        })
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {form.userType === "EMPLOYEE" ? (
              <div className="space-y-2">
                <Label>Profile Mapping</Label>
                <Select
                  value={form.profileMapping}
                  onValueChange={(value) => {
                    const selectedEmployee = employeeOptions.find((item) => item.employeeId === value)
                    setForm((prev) => ({
                      ...prev,
                      profileMapping: value,
                      companyId:
                        prev.companyId === NO_COMPANY && selectedEmployee?.companyId
                          ? selectedEmployee.companyId
                          : prev.companyId,
                    }))
                    setEmployeeSearch("")
                  }}
                >
                  <SelectTrigger className="h-auto items-start py-2 [&>span]:line-clamp-none">
                    {form.profileMapping === NO_EMPLOYEE ? (
                      <SelectValue placeholder="Select profile mapping" />
                    ) : (
                      <div className="flex flex-col text-left">
                        <span className="text-sm text-gray-900">
                          {selectedEmployee?.fullName ?? form.profileMapping}
                          {selectedEmployee ? ` (${selectedEmployee.employeeId})` : ""}
                        </span>
                      </div>
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-72 p-0">
                    <div className="sticky top-0 z-10 border-b bg-white p-2">
                      <Input
                        placeholder="ค้นหาพนักงาน"
                        value={employeeSearch}
                        onChange={(event) => setEmployeeSearch(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      />
                    </div>
                    <SelectItem value={NO_EMPLOYEE}>ไม่ระบุ</SelectItem>
                    {form.profileMapping !== NO_EMPLOYEE &&
                    !employeeOptions.some((item) => item.employeeId === form.profileMapping) ? (
                      <SelectItem value={form.profileMapping} className="items-start">
                        <div className="flex flex-col">
                          <span className="text-sm">{form.profileMapping}</span>
                        </div>
                      </SelectItem>
                    ) : null}
                    {isEmployeeLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-500">กำลังโหลด...</div>
                    ) : employeeOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">ไม่พบข้อมูล</div>
                    ) : (
                      employeeOptions.map((employee) => (
                        <SelectItem key={employee.employeeId} value={employee.employeeId} className="items-start">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-900">
                              {employee.fullName} ({employee.employeeId})
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label>Company</Label>
              <Select
                value={form.companyId}
                onValueChange={(value) => setForm((prev) => ({ ...prev, companyId: value }))}
              >
                <SelectTrigger>
                  {form.companyId === NO_COMPANY ? (
                    <SelectValue placeholder="เลือกบริษัท (ถ้ามี)" />
                  ) : (
                    <span className="line-clamp-1 text-left text-sm">{selectedCompanyLabel}</span>
                  )}
                </SelectTrigger>
                <SelectContent className="max-h-72 p-0">
                  <div className="sticky top-0 z-10 border-b bg-white p-2">
                    <Input
                      placeholder="ค้นหาบริษัท"
                      value={companySearch}
                      onChange={(event) => setCompanySearch(event.target.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                    />
                  </div>
                  <SelectItem value={NO_COMPANY}>ไม่ระบุ</SelectItem>
                  {form.companyId !== NO_COMPANY &&
                  !companyOptions.some((company) => company.companyId === form.companyId) ? (
                    <SelectItem value={form.companyId}>{selectedCompanyLabel}</SelectItem>
                  ) : null}
                  {isCompanyLoading ? (
                    <div className="px-3 py-2 text-sm text-gray-500">กำลังโหลด...</div>
                  ) : companyOptions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">ไม่พบข้อมูล</div>
                  ) : (
                    companyOptions.map((company) => (
                      <SelectItem key={company.companyId} value={company.companyId}>
                        {company.name} ({company.code})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {form.authProvider === "LOCAL" ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{isEditing ? "รหัสผ่านใหม่" : "รหัสผ่าน *"}</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                    placeholder={isEditing ? "ใส่เมื่ออยากเปลี่ยน" : "รหัสผ่าน"}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ยืนยันรหัสผ่าน {isEditing ? "" : "*"}</Label>
                  <Input
                    type="password"
                    value={form.confirmPassword}
                    onChange={(event) => setForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                    placeholder="ยืนยันรหัสผ่าน"
                  />
                </div>
              </div>
            ) : null}

            <div className="flex items-center gap-3">
              <Switch
                id="user-active"
                checked={form.isActive}
                onCheckedChange={(value) => setForm((prev) => ({ ...prev, isActive: value }))}
              />
              <Label htmlFor="user-active">ใช้งาน (Active)</Label>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              ยกเลิก
            </Button>
            <Button onClick={handleSubmit}>{isEditing ? "อัปเดต" : "บันทึก"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CreateUserAccount



